import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useEventTracking } from '../hooks/useEventTracking';
import { Button } from './ui/Button';
import { ContactInfo } from './ContactInfo';
import { CONTACT_DETAILS } from '../config/contactDetails';
import { initGA } from '../utils/analytics';

export const Header: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();
    const { trackClick } = useEventTracking();

    // Initialize Google Analytics on component mount
    useEffect(() => {
        initGA();
    }, []);

    const navigation = [
        { name: 'Home', href: '/' },
        { name: 'About', href: '/about' },
        { name: 'Methodology', href: '/methodology' },
        { name: 'Services', href: '/services' },
        { name: 'Assessment', href: '/assessment' },
        { name: 'Contact', href: '/contact' },
    ];

    const handleNavClick = (name: string) => {
        trackClick(`nav-${name.toLowerCase()}`, `Navigation - ${name}`, 'link');
        setIsMenuOpen(false);
    };

    return (
        <header className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo */}
                    <div className="flex items-center">
                        <Link
                            to="/"
                            className="flex items-center space-x-2"
                            onClick={() => handleNavClick('Logo')}
                        >
                            <img
                                src='/images/logo.webp'
                                alt={`${CONTACT_DETAILS.company.name} Logo`}
                                className="h-10 w-auto object-contain"
                            />
                            <span className="tracking-widest text-xl font-bold text-gray-900 relative group">{CONTACT_DETAILS.company.name}</span>

                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center space-x-8">
                        {navigation.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`text-sm font-medium transition-colors ${
                                    location.pathname === item.href
                                        ? 'text-primary-600'
                                        : 'text-gray-700 hover:text-primary-600'
                                }`}
                                onClick={() => handleNavClick(item.name)}
                            >
                                {item.name}
                            </Link>
                        ))}

                        <Button
                            size="sm"
                            onClick={() => {
                                trackClick('header-cta', 'Header CTA', 'button');
                                window.location.href = '/contact';
                            }}
                        >
                            Get Started
                        </Button>
                    </nav>

                    {/* Mobile menu button */}
                    <div className="md:hidden">
                        <button
                            type="button"
                            className="text-gray-700 hover:text-primary-600"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                        >
                            <span className="sr-only">Open main menu</span>
                            {isMenuOpen ? (
                                <X className="h-6 w-6"/>
                            ) : (
                                <Menu className="h-6 w-6"/>
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {isMenuOpen && (
                    <div className="md:hidden">
                        <div className="px-2 pt-2 pb-3 space-y-1 border-t border-gray-200">
                            {navigation.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    className={`block px-3 py-2 text-base font-medium transition-colors ${
                                        location.pathname === item.href
                                            ? 'text-primary-600 bg-primary-50'
                                            : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                                    }`}
                                    onClick={() => handleNavClick(item.name)}
                                >
                                    {item.name}
                                </Link>
                            ))}
                            <div className="px-3 py-2">
                                <Button
                                    size="sm"
                                    className="w-full"
                                    onClick={() => {
                                        trackClick('mobile-header-cta', 'Mobile Header CTA', 'button');
                                        setIsMenuOpen(false);
                                        window.location.href = '/contact';
                                    }}
                                >
                                    Get Started
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
};

export const Footer: React.FC = () => {
    const {trackClick} = useEventTracking();
    const year = new Date().getFullYear();

    const handleFooterClick = (item: string) => {
        trackClick(`footer-${item}`, `Footer - ${item}`, 'link');
    };

    return (
        <footer className="text-white bg-gradient-to-br from-gray-950 to-blue-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {/* Company Info */}
                    <div className="md:col-span-2">
                        <div className="flex items-center space-x-2 mb-4">
                            <img
                                src='/images/logo.webp'
                                alt={`${CONTACT_DETAILS.company.name} Logo`}
                                className="h-20 w-20 object-contain"
                            />
                            <span className="tracking-widest text-xl font-medium relative group">
                                {CONTACT_DETAILS.company.name}
                            </span>
                        </div>
                        <p className="text-gray-300 mb-4 max-w-md">
                            Predictive intelligence solutions helping organizations across
                            industries transform reactive operations into proactive strategies.
                            Monitor, Predict, Optimize, and Preserve - four elements, infinite applications.
                        </p>
                        <ContactInfo variant="footer" showPhone={false} />
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
                        <ul className="space-y-2">
                            <li>
                                <Link
                                    to="/"
                                    className="text-gray-300 hover:text-white transition-colors"
                                    onClick={() => handleFooterClick('home')}
                                >
                                    Home
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/about"
                                    className="text-gray-300 hover:text-white transition-colors"
                                    onClick={() => handleFooterClick('about')}
                                >
                                    About
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/services"
                                    className="text-gray-300 hover:text-white transition-colors"
                                    onClick={() => handleFooterClick('services')}
                                >
                                    Services
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/assessment"
                                    className="text-gray-300 hover:text-white transition-colors"
                                    onClick={() => handleFooterClick('assessment')}
                                >
                                    Free Assessment
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/contact"
                                    className="text-gray-300 hover:text-white transition-colors"
                                    onClick={() => handleFooterClick('contact')}
                                >
                                    Contact Us
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Services */}
                    <div>
                        <h3 className="text-lg font-semibold mb-4">Four Elements</h3>
                        <ul className="space-y-2 text-gray-300">
                            <li>
                                <Link
                                    to="/methodology#monitor"
                                    className="hover:text-white transition-colors"
                                    onClick={() => handleFooterClick('monitor')}
                                >
                                    Monitor
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/methodology#predict"
                                    className="hover:text-white transition-colors"
                                    onClick={() => handleFooterClick('predict')}
                                >
                                    Predict
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/methodology#optimize"
                                    className="hover:text-white transition-colors"
                                    onClick={() => handleFooterClick('optimize')}
                                >
                                    Optimize
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/methodology#preserve"
                                    className="hover:text-white transition-colors"
                                    onClick={() => handleFooterClick('preserve')}
                                >
                                    Preserve
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-gray-500 mt-8 pt-8">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <p className="text-gray-400 text-sm">© {year} {CONTACT_DETAILS.company.name}. All rights
                            reserved.</p>
                        <div className="flex space-x-6 mt-4 md:mt-0">
                            <Link
                                to="/privacy"
                                className="text-gray-400 hover:text-white text-sm transition-colors"
                                onClick={() => handleFooterClick('privacy')}
                            >
                                Privacy Policy
                            </Link>
                            <Link
                                to="/terms"
                                className="text-gray-400 hover:text-white text-sm transition-colors"
                                onClick={() => handleFooterClick('terms')}
                            >
                                Terms of Service
                            </Link>
                            <Link
                                to="/cookies"
                                className="text-gray-400 hover:text-white text-sm transition-colors"
                                onClick={() => handleFooterClick('cookies')}
                            >
                                Cookie Policy
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};